import React, { memo } from 'react';
import { useAtom } from 'jotai';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Box, CSSObject, Drawer, List, styled, SxProps, Theme, Typography, useTheme } from '@mui/material';
import {
    SignOut,
    useAppMode,
    useFeatureFlags,
    useNotes,
    useTicket,
    useTicketCountQuery,
    useUser,
} from '@tymely/services';
import { routes } from '@tymely/config';
import HistoryIcon from '@tymely/components/Icons/History';
import SettingsIcon from '@tymely/components/Icons/Settings';
import ClientsIcon from '@tymely/components/Icons/Clients';
import ExitIcon from '@tymely/components/Icons/Exit';
import SearchIcon from '@tymely/components/Icons/Search';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { NavMenu } from '@tymely/components/Menu/NavMenu';
import { SelectMenu } from '@tymely/components/Menu/SelectMenu';
import { AppMode, AppModes, drawerThemeModeAtom, ITicket, ThemeMode } from '@tymely/atoms';
import intersection from 'lodash/intersection';
import sum from 'lodash/sum';

import { sidebarMenuItems } from './sidebarMenuItems';
import LabeledSwitch from './components/LabeledSwitch';
import TicketSearch from './components/TicketSearch';
import ChannelSelector from './components/ChannelSelector';
import TicketHandlingModeSelector from './components/TicketHandlingModeSelector';
import SideBarItem from './components/SideBarItem';
import LogoItem from './components/LogoItem';
import TicketHistoryDrawer from './components/TicketHistoryDrawer';

const HistoryItem = React.memo(
    (props: { active: boolean; warning?: React.ReactNode; onActive: (active: boolean) => void }) => {
        return <SideBarItem title="History & Notes" icon={<HistoryIcon />} {...props} />;
    },
);

HistoryItem.displayName = 'HistoryItem';
type SettingsItemProps = { onNavigate?: () => void };

const ManagementSubMenu = ({ onNavigate }: SettingsItemProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useUser();
    const navOptions = sidebarMenuItems.map((item) => {
        const unauthorized = intersection(item.allowedRoles, user?.roles ?? []).length === 0;
        return {
            ...item,
            value: item.route,
            disabled: unauthorized,
            title: unauthorized ? 'Unauthorized' : undefined,
        };
    });

    const value = React.useMemo(
        () => sidebarMenuItems.find(({ route }) => route === location.pathname)?.route,
        [location.pathname],
    );

    const _onNavigate = (value: string) => {
        navigate(value);
        onNavigate?.();
    };

    return (
        <SideBarItem title="Manage" icon={<SettingsIcon />} hasMenu>
            {(anchorEl, open, setClose) => (
                <NavMenu
                    anchorEl={anchorEl}
                    open={open}
                    value={value}
                    options={navOptions}
                    onSelect={(path) => {
                        _onNavigate(path);
                        setClose();
                    }}
                    onClose={setClose}
                />
            )}
        </SideBarItem>
    );
};

const ChannelsItem = () => {
    const { isChat } = useAppMode();
    const ticketCountQuery = useTicketCountQuery();
    const ticketCount = sum((ticketCountQuery.data ?? []).map((qi) => qi.ticket_count));

    return (
        <SideBarItem
            title="Channels"
            icon={
                <Badge badgeContent={ticketCount} color="primary">
                    <ClientsIcon />
                </Badge>
            }
            hasMenu
            disabled={isChat}
        >
            {(anchorEl, open, setClose) => <ChannelSelector anchorEl={anchorEl} open={open} onClose={setClose} />}
        </SideBarItem>
    );
};

const ShiftItem = (props: { sx?: SxProps }) => {
    const { switchShift, shiftEnd, isChat } = useAppMode();
    const onChange = React.useCallback(() => {
        switchShift();
    }, [switchShift]);
    return (
        <SideBarItem
            disabled={isChat}
            title="End online mode"
            sx={props.sx}
            icon={<LabeledSwitch checked={!shiftEnd} onLabel="On" offLabel="EOS" onChange={onChange} />}
        />
    );
};

const TicketSearchMenuItem = () => {
    return (
        <SideBarItem title="Ticket Search" icon={<SearchIcon />} hasMenu>
            {(anchorEl, open, setClose) => <TicketSearch anchorEl={anchorEl} open={open} onClose={setClose} />}
        </SideBarItem>
    );
};

const FeaturesItem = () => {
    const { features, enabledFeatureFlags: enabledFeatures, toggleFeatureFlag: toggleFeature } = useFeatureFlags();

    const options = React.useMemo(
        () => Object.entries(features).map(([key, value]) => ({ label: value, value: key })),
        [features],
    );

    const onSelect = React.useCallback((vals: string[]) => {
        for (const feature in features) {
            toggleFeature(feature, vals.includes(feature));
        }
    }, []);

    return (
        <SideBarItem title="Features" icon={<ScienceOutlinedIcon />} hasMenu>
            {(anchorEl, open, setClose) => (
                <SelectMenu
                    anchorEl={anchorEl}
                    open={open}
                    values={enabledFeatures}
                    options={options}
                    multiple
                    showTooltip
                    variant="nav"
                    onSelect={onSelect}
                    onClose={setClose}
                />
            )}
        </SideBarItem>
    );
};

const ThemeModeItem = memo(() => {
    const [mode, setMode] = useAtom(drawerThemeModeAtom);

    const handleToggle = React.useCallback(() => {
        setMode((currentMode: ThemeMode) => (currentMode === 'light' ? 'dark' : 'light'));
    }, [setMode]);

    return (
        <SideBarItem
            title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
            icon={mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            onClick={handleToggle}
        />
    );
});
ThemeModeItem.displayName = 'ThemeModeItem';

const openedMixin = (theme: Theme): CSSObject => ({
    backgroundColor: 'transparent',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    width: theme.spacing(59.5),
    flexDirection: 'row',
    overflow: 'visible',
    border: 0,
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(11.5),
    overflow: 'visible',
    border: 0,
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
            ...openedMixin(theme),
        },
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

const NavList = styled(List)(
    ({ theme }) => `
    padding: 0;
    background-color: ${theme.nav.default};
    width: ${theme.spacing(11.5)};
`,
);

export const AppSideBar = React.memo(() => {
    const theme = useTheme();
    const ticket = useTicket();
    const location = useLocation();
    const isTicketRoute = Boolean(
        matchRoutes([{ path: `${routes.ticket}/*` }, { path: `${routes.onlineTicket}/*` }], location),
    );
    const isGraphPolicyEditor = Boolean(matchRoutes([{ path: `${routes.graphPolicyEditor}/*` }], location));
    const { appMode, isOnline } = useAppMode();
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const onClose = React.useCallback(() => setDrawerOpen(false), []);
    const [recentTickets, setRecentTickets] = React.useState<ITicket[]>([]);
    const { features } = useFeatureFlags();
    const hasFeatures = Object.keys(features).length > 0;

    const onActive = React.useCallback(() => {
        setDrawerOpen((active) => !active);
        setRecentTickets([]);
    }, []);

    const { notes } = useNotes();
    React.useEffect(() => {
        if (notes.length > 0 && ![AppMode.Admin, AppMode.Analyst].includes(appMode!)) {
            setDrawerOpen(true);
        }
    }, [notes, setDrawerOpen]);

    return (
        <StyledDrawer variant="permanent" open={isTicketRoute && ticket && drawerOpen}>
            <Box
                display="flex"
                zIndex={theme.zIndex.appBar}
                flexDirection="column"
                justifyContent="space-between"
                height={1}
            >
                <NavList sx={{ height: theme.spacing(28), pt: 4.5 }}>
                    <TicketHandlingModeSelector />
                    {appMode && (
                        <Typography
                            variant="body2"
                            color="common.white"
                            textTransform="capitalize"
                            textAlign="center"
                            component="div"
                            mb={3}
                        >
                            {AppModes[appMode]}
                        </Typography>
                    )}
                    {isOnline && <ShiftItem sx={{ pt: 0, pb: 0 }} />}
                </NavList>
                <NavList sx={{ flex: 1 }}>
                    {(isTicketRoute || appMode === AppMode.Online) && ticket && (
                        <HistoryItem
                            active={drawerOpen}
                            onActive={onActive}
                            warning={
                                recentTickets.length && (
                                    <Box>
                                        {'Relevant'}
                                        <br />
                                        {'context?'}
                                    </Box>
                                )
                            }
                        />
                    )}
                    {isTicketRoute && <ChannelsItem />}
                    <TicketSearchMenuItem />
                    <ManagementSubMenu onNavigate={onClose} />
                    {hasFeatures && <FeaturesItem />}
                    {isGraphPolicyEditor && <ThemeModeItem />}
                </NavList>
                <NavList>
                    <SideBarItem title="Logout" icon={<ExitIcon />} onClick={SignOut} />
                    <LogoItem />
                </NavList>
            </Box>
            {isTicketRoute && ticket && (
                <TicketHistoryDrawer
                    open={drawerOpen}
                    onClose={onClose}
                    onRecentTickets={(tickets) => {
                        setRecentTickets(tickets);
                    }}
                />
            )}
        </StyledDrawer>
    );
});

AppSideBar.displayName = 'AppSideBar';
export default AppSideBar;
